import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import useScreenName from "../../hooks/useScreenName";
import DatafieldSection from "../../components/datafield-section";
import LabeledSwitch from "../../components/switch/LabeledSwitch";
import { useSelector, useDispatch } from "react-redux";
import { ReduxState } from "../../reducers";
import { Employer, EmployerSettings, EmployerConfig } from "../../reducers/employer.reducer";
import { dateToDDMMYYYY } from "../../utils";
import dispatch from "../../middleware";
import {
  getEmployer,
  getEmployerSettings,
  updateEmployerSettings,
  updateEmployerConfig,
} from "../../actions/employer.action";
import { listPricings } from "../../actions/credit-decision.action";
import Button from "../../components/button";
import { EmployerActions } from "../../actions/employer.action";
import Alert from "../../components/alert";
import { ReactComponent as Loader } from "../../images/loader.svg";
import SearchableMultiSelect, {
  MultiSelectOption,
} from "../../components/searchable-multi-select";
import { RoutePaths } from "../../models";
import {
  EwaCreditDecisionConfig,
  PricingConfig,
  UpdateEmployerConfigRequest,
  SalaryTopupDecisionConfig,
  RafikiDecisionTreeModelEnum,
  ManualReviewConfig,
  ApplicabilityMetadata,
  CreditDecisionPricing,
  CreditDecisionPricingListResponse,
} from "../../models/creditDecision";

const EMPTY_EWA_CONFIG: EwaCreditDecisionConfig = {
  isEnabled: false,
  maxWithdrawalPerTransaction: undefined,
  maxWithdrawalPerDay: undefined,
  maxWithdrawalAmountPerCycle: undefined,
  maxTransactionsPerCycle: undefined,
  maxTransactionsPerCalendarYear: undefined,
  probationPeriodInDays: undefined,
};

const ALL_ML_MODELS: RafikiDecisionTreeModelEnum[] = Object.values(
  RafikiDecisionTreeModelEnum
);

const ML_MODEL_OPTIONS: MultiSelectOption[] = ALL_ML_MODELS.map((model) => ({
  value: model,
  text: model,
}));

const EMPTY_PRICING_CONFIG: PricingConfig = {
  pricingId: "",
};

const EMPTY_APPLICABILITY_METADATA: ApplicabilityMetadata = {
  strategy: "USER_ID_SUFFIX",
  userIdSuffixes: [],
};

const EMPTY_MANUAL_REVIEW: ManualReviewConfig = {
  manualReviewRequired: false,
  applicabilityMetadata: { ...EMPTY_APPLICABILITY_METADATA },
};

const EMPTY_SALARY_TOPUP: SalaryTopupDecisionConfig = {
  loanConstraints: {
    amount: { min: undefined, max: undefined },
    tenureInMonths: { min: undefined, max: undefined },
    maxFoirThresholdPercentage: undefined,
  },
  maxAnnualPercentageRate: undefined,
  mlModels: [] as RafikiDecisionTreeModelEnum[],
  manualReview: { ...EMPTY_MANUAL_REVIEW },
};

const EMPTY_CONFIG: EmployerConfig = {
  ewaCreditDecisionConfig: { ...EMPTY_EWA_CONFIG },
  pricingConfig: { ...EMPTY_PRICING_CONFIG },
  salaryTopupDecisionConfig: { ...EMPTY_SALARY_TOPUP },
};

function normalizeManualReview(
  manualReview?: ManualReviewConfig
): { manualReviewRequired: boolean; strategy: string; userIdSuffixes: string[] } | undefined {
  if (!manualReview) return undefined;

  const userIdSuffixes = (
    manualReview.applicabilityMetadata?.userIdSuffixes ?? []
  ).filter(Boolean);
  const strategy =
    manualReview.applicabilityMetadata?.strategy ?? "USER_ID_SUFFIX";

  if (!manualReview.manualReviewRequired && userIdSuffixes.length === 0) {
    return undefined;
  }

  return {
    manualReviewRequired: manualReview.manualReviewRequired,
    strategy,
    userIdSuffixes,
  };
}

const EmployerDetails = () => {
  const { id } = useParams() as { id: string };
  useScreenName(`Employer Details - ${id}`);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const storeDispatch = useDispatch();
  const employerData = useSelector((state: ReduxState) =>
    state.employer.employers.find((emp) => emp._id === id)
  ) as Employer | undefined;

  useEffect(() => {
    dispatch(storeDispatch, getEmployer(id))
      .then((employer: Employer | null) => {
        if (!employer) {
          setErrorMessage("Employer not found");
          return;
        }
        setErrorMessage(null);
      })
      .catch(() => {
        setErrorMessage("Failed to fetch employer details");
      });
    dispatch(storeDispatch, getEmployerSettings(id))
      .then((settings) => {
        setFormState(settings?.ewa);
      })
      .catch(() => {
        setErrorMessage("Failed to fetch employer settings");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeDispatch, id]);

  // --- Underwriting config (existing /underwriting-config endpoint) ---
  const [formState, setFormState] = useState<EmployerSettings>({
    maxWithdrawalSalaryPercent: null,
    isDisbursalAllowed: false,
    requiredAgeLimit: {
      min: null,
      max: null,
    },
    minRequiredEmploymentDurationInDays: null,
    minRequiredSalary: null,
  });

  const [isEditMode, setIsEditMode] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormState((prevState) => {
      const keys = name.split(".");
      if (keys.length > 1) {
        const [parentKey, childKey] = keys as [keyof EmployerSettings, string];
        return {
          ...prevState,
          [parentKey]: {
            ...(prevState[parentKey] as Record<string, any>),
            [childKey]: type === "checkbox" ? checked : value,
          },
        };
      }
      return {
        ...prevState,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const toggleEditMode = () => {
    setIsEditMode((prevMode) => !prevMode);
  };

  const handleSave = () => {
    const sanitizedFormState: Record<any, any> = {
      ...formState,
      maxWithdrawalSalaryPercent: formState.maxWithdrawalSalaryPercent ? Number(formState.maxWithdrawalSalaryPercent) : null,
      requiredAgeLimitMin: formState.requiredAgeLimit.min ? Number(formState.requiredAgeLimit.min) : null,
      requiredAgeLimitMax: formState.requiredAgeLimit.max ? Number(formState.requiredAgeLimit.max) : null,
      minRequiredEmploymentDurationInDays: formState.minRequiredEmploymentDurationInDays ? Number(formState.minRequiredEmploymentDurationInDays) : null,
      minRequiredSalary: formState.minRequiredSalary ? Number(formState.minRequiredSalary) : null,
    };

    delete sanitizedFormState.requiredAgeLimit;

    dispatch(storeDispatch, updateEmployerSettings(id, sanitizedFormState))
      .then(() => {
        setIsEditMode(false);
        setErrorMessage(null);
      })
      .catch(() => {
        setErrorMessage("Failed to update employer settings");
      });
  };

  const isLoading = useSelector((state: ReduxState) =>
    state.employer.loadingQueue.includes(EmployerActions.REQUEST_UPDATE_EMPLOYER_SETTINGS)
  );
  const [isPricingEditMode, setIsPricingEditMode] = useState(false);
  const [pricings, setPricings] = useState<CreditDecisionPricing[]>([]);
  const pricingsFetchedRef = useRef(false);

  // --- Credit Decision config (/update-config endpoint) ---
  const [configForm, setConfigForm] = useState<EmployerConfig>(EMPTY_CONFIG);
  const [initialManualReview, setInitialManualReview] = useState<
    ManualReviewConfig | undefined
  >(undefined);
  const [isSalaryTopupEditMode, setIsSalaryTopupEditMode] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [userIdSuffixesText, setUserIdSuffixesText] = useState("");

  useEffect(() => {
    if (employerData?.config) {
      const loadedManualReview =
        employerData.config.salaryTopupDecisionConfig?.manualReview;
      setInitialManualReview(loadedManualReview);
      setUserIdSuffixesText(
        (loadedManualReview?.applicabilityMetadata?.userIdSuffixes ?? []).join(
          ", "
        )
      );
      setConfigForm({
        ewaCreditDecisionConfig: {
          ...EMPTY_EWA_CONFIG,
          ...employerData.config.ewaCreditDecisionConfig,
        },
        pricingConfig: {
          ...EMPTY_PRICING_CONFIG,
          ...employerData.config.pricingConfig,
        },
        salaryTopupDecisionConfig: {
          ...EMPTY_SALARY_TOPUP,
          ...employerData.config.salaryTopupDecisionConfig,
          loanConstraints: {
            ...EMPTY_SALARY_TOPUP.loanConstraints,
            ...(employerData.config.salaryTopupDecisionConfig
              ?.loanConstraints || {}),
          },
          mlModels:
            employerData.config.salaryTopupDecisionConfig?.mlModels ?? [],
          manualReview: {
            ...EMPTY_MANUAL_REVIEW,
            ...(employerData.config.salaryTopupDecisionConfig?.manualReview ||
              {}),
            applicabilityMetadata: {
              ...EMPTY_APPLICABILITY_METADATA,
              ...(employerData.config.salaryTopupDecisionConfig?.manualReview
                ?.applicabilityMetadata || {}),
            },
          },
        },
      });
    }
  }, [employerData]);

  useEffect(() => {
    if (pricingsFetchedRef.current) return;
    pricingsFetchedRef.current = true;
    dispatch(storeDispatch, listPricings({ limit: 1000 }))
      .then((response: CreditDecisionPricingListResponse) =>
        setPricings(response.data)
      )
      .catch(() => {});
  }, [storeDispatch]);

  function updateLoanConstraintField(
    group: "amount" | "tenureInMonths",
    field: "min" | "max",
    value: number | undefined
  ) {
    setConfigForm((prev) => {
      const st = prev.salaryTopupDecisionConfig || EMPTY_SALARY_TOPUP;
      const lc = st.loanConstraints || {};
      return {
        ...prev,
        salaryTopupDecisionConfig: {
          ...st,
          loanConstraints: {
            ...lc,
            [group]: {
              ...(lc[group] || {}),
              [field]: value,
            },
          },
        },
      };
    });
  }

  function updateManualReviewField(
    field: keyof ManualReviewConfig,
    value: boolean | ApplicabilityMetadata
  ) {
    setConfigForm((prev) => {
      const st = prev.salaryTopupDecisionConfig || EMPTY_SALARY_TOPUP;
      const mr = st.manualReview || { ...EMPTY_MANUAL_REVIEW };
      return {
        ...prev,
        salaryTopupDecisionConfig: {
          ...st,
          manualReview: {
            ...mr,
            [field]: value,
          },
        },
      };
    });
  }

  function updatePricingField(field: keyof PricingConfig, value: any) {
    setConfigForm((prev) => ({
      ...prev,
      pricingConfig: {
        ...(prev.pricingConfig || EMPTY_PRICING_CONFIG),
        [field]: value,
      },
    }));
  }

  function updateSalaryTopupField(
    field: keyof SalaryTopupDecisionConfig,
    value: any
  ) {
    setConfigForm((prev) => ({
      ...prev,
      salaryTopupDecisionConfig: {
        ...(prev.salaryTopupDecisionConfig || EMPTY_SALARY_TOPUP),
        [field]: value,
      },
    }));
  }

  function buildPricingPayload(): UpdateEmployerConfigRequest {
    const payload: UpdateEmployerConfigRequest = {};
    const nextPricingId = configForm.pricingConfig?.pricingId || "";
    const currentPricingId = employerData?.config?.pricingConfig?.pricingId || "";
    if (nextPricingId && nextPricingId !== currentPricingId) {
      payload.pricingConfig = { pricingId: nextPricingId };
    }
    return payload;
  }

  function updateApplicabilityField(
    field: keyof ApplicabilityMetadata,
    value: string | string[]
  ) {
    setConfigForm((prev) => {
      const st = prev.salaryTopupDecisionConfig || EMPTY_SALARY_TOPUP;
      const mr = st.manualReview || { ...EMPTY_MANUAL_REVIEW };
      const am = mr.applicabilityMetadata || { ...EMPTY_APPLICABILITY_METADATA };
      return {
        ...prev,
        salaryTopupDecisionConfig: {
          ...st,
          manualReview: {
            ...mr,
            applicabilityMetadata: {
              ...am,
              [field]: value,
            },
          },
        },
      };
    });
  }

  function buildSalaryTopupPayload(): UpdateEmployerConfigRequest {
    const payload: UpdateEmployerConfigRequest = {};

    const st = configForm.salaryTopupDecisionConfig;
    if (st) {
      const stPayload: SalaryTopupDecisionConfig = {};

      if (st.loanConstraints) {
        const lc: any = {};
        if (
          st.loanConstraints.amount &&
          (st.loanConstraints.amount.min !== undefined ||
            st.loanConstraints.amount.max !== undefined)
        ) {
          lc.amount = {};
          if (st.loanConstraints.amount.min !== undefined)
            lc.amount.min = Number(st.loanConstraints.amount.min);
          if (st.loanConstraints.amount.max !== undefined)
            lc.amount.max = Number(st.loanConstraints.amount.max);
        }
        if (
          st.loanConstraints.tenureInMonths &&
          (st.loanConstraints.tenureInMonths.min !== undefined ||
            st.loanConstraints.tenureInMonths.max !== undefined)
        ) {
          lc.tenureInMonths = {};
          if (st.loanConstraints.tenureInMonths.min !== undefined)
            lc.tenureInMonths.min = Number(
              st.loanConstraints.tenureInMonths.min
            );
          if (st.loanConstraints.tenureInMonths.max !== undefined)
            lc.tenureInMonths.max = Number(
              st.loanConstraints.tenureInMonths.max
            );
        }
        if (st.loanConstraints.maxFoirThresholdPercentage !== undefined) {
          lc.maxFoirThresholdPercentage = Number(
            st.loanConstraints.maxFoirThresholdPercentage
          );
        }
        if (Object.keys(lc).length > 0) {
          stPayload.loanConstraints = lc;
        }
      }

      if (st.maxAnnualPercentageRate !== undefined) {
        stPayload.maxAnnualPercentageRate = Number(st.maxAnnualPercentageRate);
      }

      if (st.mlModels && st.mlModels.length > 0) {
        stPayload.mlModels = st.mlModels;
      }

      const currentManualReview = normalizeManualReview(st.manualReview);
      const loadedManualReview = normalizeManualReview(initialManualReview);
      if (
        JSON.stringify(currentManualReview) !==
        JSON.stringify(loadedManualReview)
      ) {
        const currentSuffixes = currentManualReview?.userIdSuffixes ?? [];
        stPayload.manualReview = {
          manualReviewRequired: st.manualReview?.manualReviewRequired ?? false,
          applicabilityMetadata: {
            strategy: "USER_ID_SUFFIX",
            userIdSuffixes: currentSuffixes,
          },
        };
      }

      if (Object.keys(stPayload).length > 0) {
        payload.salaryTopupDecisionConfig = stPayload;
      }
    }
    return payload;
  }

  function saveConfigSection(
    builder: () => UpdateEmployerConfigRequest,
    onSuccess: () => void
  ) {
    setConfigError(null);
    const payload = builder();
    if (Object.keys(payload).length === 0) {
      onSuccess();
      return;
    }

    dispatch(storeDispatch, updateEmployerConfig(id, payload))
      .then(() => {
        onSuccess();
        setConfigError(null);
        dispatch(storeDispatch, getEmployer(id));
      })
      .catch(() => {
        setConfigError("Failed to update credit decision config");
      });
  }

  const isConfigLoading = useSelector((state: ReduxState) =>
    state.employer.loadingQueue.includes(EmployerActions.REQUEST_UPDATE_EMPLOYER_CONFIG)
  );

  if (errorMessage) {
    return <Alert type="danger" message={errorMessage} />;
  }

  if (!employerData) {
    return <Loader />;
  }

  const employerFields = Object.entries(employerData)
    .filter(([key]) => ["name", "clientId", "lenderId", "clientRefId"].includes(key))
    .map(([key, value]) => ({
      key,
      label: `${key.charAt(0).toUpperCase() + key.slice(1)}:`,
      value: key.toLowerCase().includes("date") && value ? dateToDDMMYYYY(value) : value || "N/A",
    }));

  return (
    <>
      <div className="dashboard-container mb-4">
        <DatafieldSection
          title="Basic Details"
          labels={[
            {
              items: employerFields.map((field) => ({
                key: field.key,
                label: field.label,
              })),
            },
          ]}
          data={employerFields.reduce((acc, field) => {
            acc[field.key] = field.value;
            return acc;
          }, {} as Record<string, string>)}
        />
      </div>

      <div className="dashboard-container">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h3 className="user-page-title mb-0">
            Credit Decision Configuration
          </h3>
        </div>

        {configError && <Alert type="danger" message={configError} />}

        <div className="dashboard-container mb-4 border rounded p-3">
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">Disbursal Control</h5>
              <small className="text-muted">
                Blocks loan disbursal for this employer across both EWA and
                Salary Topup products.
              </small>
            </div>
            <Button
              onClick={isEditMode ? handleSave : toggleEditMode}
              variant="blue"
              size="sm"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : isEditMode ? "Save" : "Edit"}
            </Button>
          </div>
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-4 d-flex align-items-end">
              <LabeledSwitch
                id="isDisbursalAllowed"
                name="isDisbursalAllowed"
                label="Disbursal Allowed"
                checked={formState.isDisbursalAllowed}
                onChange={handleInputChange}
                disabled={!isEditMode}
              />
            </div>
          </div>
        </div>

        <div className="dashboard-container mb-4 border rounded p-3">
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">EWA Credit Decision</h5>
              <small className="text-muted">
                Rules used for EWA eligibility and withdrawal limits.
              </small>
            </div>
            <small className={`text-muted ${isEditMode ? "" : "invisible"}`}>
              Editing enabled
            </small>
          </div>
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label">
                Max Withdrawal Salary Percent
              </label>
              <input
                type="number"
                className="form-control"
                name="maxWithdrawalSalaryPercent"
                placeholder="Enter percentage"
                min={0}
                max={100}
                value={formState.maxWithdrawalSalaryPercent ?? ""}
                onChange={handleInputChange}
                disabled={!isEditMode}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label">Minimum User Age</label>
              <input
                type="number"
                className="form-control"
                name="requiredAgeLimit.min"
                placeholder="Enter minimum age"
                min={1}
                value={formState.requiredAgeLimit.min ?? ""}
                onChange={handleInputChange}
                disabled={!isEditMode}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label">Maximum User Age</label>
              <input
                type="number"
                className="form-control"
                name="requiredAgeLimit.max"
                placeholder="Enter maximum age"
                value={formState.requiredAgeLimit.max ?? ""}
                onChange={handleInputChange}
                disabled={!isEditMode}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label">
                Minimum Employment Duration (Days)
              </label>
              <input
                type="number"
                className="form-control"
                name="minRequiredEmploymentDurationInDays"
                placeholder="Enter duration in days"
                min={1}
                value={formState.minRequiredEmploymentDurationInDays ?? ""}
                onChange={handleInputChange}
                disabled={!isEditMode}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label">Minimum Required Salary</label>
              <input
                type="number"
                className="form-control"
                name="minRequiredSalary"
                placeholder="Enter minimum salary"
                min={0}
                value={formState.minRequiredSalary ?? ""}
                onChange={handleInputChange}
                disabled={!isEditMode}
              />
            </div>
          </div>
          <div className="mb-4 pt-3 border-top">
            <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
              <h5 className="mb-0">Pricing</h5>
              <Button
                onClick={() =>
                  isPricingEditMode
                    ? saveConfigSection(buildPricingPayload, () =>
                        setIsPricingEditMode(false)
                      )
                    : setIsPricingEditMode(true)
                }
                variant="blue"
                size="sm"
                disabled={isConfigLoading}
              >
                {isConfigLoading
                  ? "Saving..."
                  : isPricingEditMode
                  ? "Save"
                  : "Edit"}
              </Button>
            </div>
            {(() => {
              const selectedPricing = pricings.find(
                (p) => p._id === configForm.pricingConfig?.pricingId
              );
              const pricingLabel = selectedPricing
                ? selectedPricing.name
                : configForm.pricingConfig?.pricingId || "None";
              const pricingOptions: MultiSelectOption[] = [
                { value: "", text: "None" },
                ...pricings.map((p) => ({
                  value: p._id,
                  text: p.name,
                  html: `<strong>${p.name}</strong><br><small style="color: #666;">${p._id}</small>`,
                })),
              ];
              return isPricingEditMode ? (
                <SearchableMultiSelect
                  options={pricingOptions}
                  value={configForm.pricingConfig?.pricingId ? [configForm.pricingConfig.pricingId] : []}
                  onSelect={(value) => updatePricingField("pricingId", String(value))}
                  onUnselect={() => updatePricingField("pricingId", "")}
                  placeholder="Select pricing..."
                  maxSelections={1}
                  search
                  showBadges
                />
              ) : (
                <div>
                  {configForm.pricingConfig?.pricingId ? (
                    <Link to={RoutePaths.CREDIT_DECISION_PRICING_DETAIL.replace(":id", configForm.pricingConfig.pricingId)}>
                      {pricingLabel} →
                    </Link>
                  ) : (
                    <span className="text-muted">None</span>
                  )}
                </div>
              );
            })()}
            <small className="text-muted d-block mt-1">
              Pricing plan applied to EWA decisions.
            </small>
          </div>

        </div>

        <div className="dashboard-container border rounded p-3">
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">Salary Topup Decision</h5>
              <small className="text-muted">
                Tune loan limits, decision thresholds, and manual review rules.
              </small>
            </div>
            <Button
              onClick={() =>
                isSalaryTopupEditMode
                  ? saveConfigSection(buildSalaryTopupPayload, () =>
                      setIsSalaryTopupEditMode(false)
                    )
                  : setIsSalaryTopupEditMode(true)
              }
              variant="blue"
              size="sm"
              disabled={isConfigLoading}
            >
              {isConfigLoading
                ? "Saving..."
                : isSalaryTopupEditMode
                ? "Save"
                : "Edit"}
            </Button>
          </div>

          <div className="mb-4">
            <h6 className="mb-2">Loan Constraints</h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">Amount Min</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 1000"
                  min={0}
                  value={
                    configForm.salaryTopupDecisionConfig?.loanConstraints
                      ?.amount?.min ?? ""
                  }
                  onChange={(e) =>
                    updateLoanConstraintField(
                      "amount",
                      "min",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  disabled={!isSalaryTopupEditMode}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Amount Max</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 50000"
                  min={0}
                  value={
                    configForm.salaryTopupDecisionConfig?.loanConstraints
                      ?.amount?.max ?? ""
                  }
                  onChange={(e) =>
                    updateLoanConstraintField(
                      "amount",
                      "max",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  disabled={!isSalaryTopupEditMode}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Tenure Min (Months)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 3"
                  min={0}
                  value={
                    configForm.salaryTopupDecisionConfig?.loanConstraints
                      ?.tenureInMonths?.min ?? ""
                  }
                  onChange={(e) =>
                    updateLoanConstraintField(
                      "tenureInMonths",
                      "min",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  disabled={!isSalaryTopupEditMode}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Tenure Max (Months)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 24"
                  min={0}
                  value={
                    configForm.salaryTopupDecisionConfig?.loanConstraints
                      ?.tenureInMonths?.max ?? ""
                  }
                  onChange={(e) =>
                    updateLoanConstraintField(
                      "tenureInMonths",
                      "max",
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                  disabled={!isSalaryTopupEditMode}
                />
              </div>
            </div>
          </div>

          <div className="mb-4 pt-3 border-top">
            <h5 className="mb-2">ML Models</h5>
            <SearchableMultiSelect
              options={ML_MODEL_OPTIONS}
              value={configForm.salaryTopupDecisionConfig?.mlModels || []}
              onChange={(selected) =>
                updateSalaryTopupField("mlModels", selected.map(String))
              }
              placeholder="Select ML models..."
              disabled={!isSalaryTopupEditMode}
              search
              selectAll
              showBadges
            />
          </div>

          <div className="mb-4 pt-3 border-top">
            <h5 className="mb-2">Decisioning</h5>
            <small className="text-muted d-block mb-2">
              Loan approval decisions are actually driven by the policy
              attachments linked below, not by the fields above alone. Review or
              update the active policies for this employer here.
            </small>
            <Link
              to={`${RoutePaths.UNDERWRITING_POLICY_ATTACHMENTS}?employerIds=${employerData._id}`}
            >
              View →
            </Link>
          </div>

          <div>
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <LabeledSwitch
                  id="manualReviewRequired"
                  label="Manual Review Required"
                  name="manualReviewRequired"
                  checked={
                    configForm.salaryTopupDecisionConfig?.manualReview
                      ?.manualReviewRequired ?? false
                  }
                  onChange={(e) =>
                    updateManualReviewField(
                      "manualReviewRequired",
                      e.target.checked
                    )
                  }
                  disabled={!isSalaryTopupEditMode}
                />
                <small className="text-muted d-block mt-1">
                  Route matching cases to manual review.
                </small>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">
                  User ID Suffixes to move to manual
                </label>
                <textarea
                  className="form-control"
                  placeholder="Enter comma-separated user ID suffixes"
                  value={userIdSuffixesText}
                  onChange={(e) => {
                    const text = e.target.value;
                    setUserIdSuffixesText(text);
                    updateApplicabilityField(
                      "userIdSuffixes",
                      text
                        .split(",")
                        .map((suffix) => suffix.trim())
                        .filter(Boolean)
                    );
                  }}
                  disabled={!isSalaryTopupEditMode}
                  rows={2}
                />
                <small className="text-muted">
                  Enter comma-separated user ID suffixes, empty means all
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployerDetails;
